import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiInfo } from 'react-icons/fi';

const OrderForm = ({ symbol, onOrderPlaced }) => {
  const [formData, setFormData] = useState({
    orderType: 'limit',
    side: 'buy',
    quantity: '',
    limitPrice: '',
    stopPrice: '',
    trailingPercent: '',
  });
  
  const [marketData, setMarketData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (symbol) {
      fetchMarketData();
    }
  }, [symbol]);

  const fetchMarketData = async () => {
    try {
      const response = await axios.get(`/api/v1/market-data?symbols=${symbol}`);
      setMarketData(response.data.data[symbol]);
    } catch (err) {
      console.error('Failed to fetch market data:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateOrderValue = () => {
    if (!formData.quantity) return 0;
    const price = formData.limitPrice || (marketData?.last || 0);
    return (parseFloat(formData.quantity) * parseFloat(price)).toFixed(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      let endpoint = '';
      let payload = {
        symbol,
        quantity: parseFloat(formData.quantity),
        side: formData.side
      };

      switch (formData.orderType) {
        case 'limit':
          endpoint = '/orders/limit';
          payload.limit_price = parseFloat(formData.limitPrice);
          break;
        case 'stop':
          endpoint = '/orders/stop';
          payload.stop_price = parseFloat(formData.stopPrice);
          break;
        case 'stop_limit':
          endpoint = '/orders/stop-limit';
          payload.stop_price = parseFloat(formData.stopPrice);
          payload.limit_price = parseFloat(formData.limitPrice);
          break;
        case 'trailing_stop':
          endpoint = '/orders/trailing-stop';
          payload.trailing_percent = parseFloat(formData.trailingPercent);
          break;
      }

      const response = await axios.post(`/api/v1${endpoint}`, payload);
      
      setSuccess(`Order placed successfully! Order ID: ${response.data.order_id}`);
      
      if (onOrderPlaced) {
        onOrderPlaced();
      }
      
      // Reset form
      setFormData({
        orderType: 'limit',
        side: 'buy',
        quantity: '',
        limitPrice: '',
        stopPrice: '',
        trailingPercent: '',
      });
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card max-w-4xl mx-auto">
      <h2 className="text-xl font-bold text-gray-900 mb-4">Place Order - {symbol}</h2>
      
      {/* Market Data */}
      {marketData && (
        <div className="mb-6 p-3 bg-gray-50 rounded-lg">
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center">
              <span className="text-sm text-gray-600 mr-2">Last:</span>
              <span className="font-semibold">${marketData.last}</span>
            </div>
            <div className="flex items-center">
              <span className="text-sm text-gray-600 mr-2">Bid:</span>
              <span className="font-semibold">${marketData.bid}</span>
            </div>
            <div className="flex items-center">
              <span className="text-sm text-gray-600 mr-2">Ask:</span>
              <span className="font-semibold">${marketData.ask}</span>
            </div>
            <div className="flex items-center">
              <span className="text-sm text-gray-600 mr-2">Change:</span>
              <span className={`font-semibold ${marketData.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {marketData.change >= 0 ? '+' : ''}{marketData.change} ({marketData.changePercent}%)
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Order Type */}
          <div>
            <label className="label">Order Type</label>
            <select
              name="orderType"
              value={formData.orderType}
              onChange={handleChange}
              className="select"
            >
              <option value="limit">Limit</option>
              <option value="market">Market</option>
              <option value="stop">Stop</option>
              <option value="stop_limit">Stop Limit</option>
              <option value="trailing_stop">Trailing Stop</option>
            </select>
          </div>

          {/* Side */}
          <div>
            <label className="label">Side</label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="side"
                  value="buy"
                  checked={formData.side === 'buy'}
                  onChange={handleChange}
                  className="mr-2"
                />
                <span className={`px-4 py-2 rounded ${formData.side === 'buy' ? 'bg-green-100 text-green-800 font-medium' : 'bg-gray-100 text-gray-700'}`}>
                  Buy
                </span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="side"
                  value="sell"
                  checked={formData.side === 'sell'}
                  onChange={handleChange}
                  className="mr-2"
                />
                <span className={`px-4 py-2 rounded ${formData.side === 'sell' ? 'bg-red-100 text-red-800 font-medium' : 'bg-gray-100 text-gray-700'}`}>
                  Sell
                </span>
              </label>
            </div>
          </div>

          {/* Quantity */}
          <div>
            <label className="label">Quantity</label>
            <input
              type="number"
              name="quantity"
              value={formData.quantity}
              onChange={handleChange}
              className="input"
              placeholder="Enter quantity"
              step="1"
              min="1"
              required
            />
          </div>

          {/* Limit Price */}
          {formData.orderType === 'limit' && (
            <div>
              <label className="label">Limit Price ($)</label>
              <input
                type="number"
                name="limitPrice"
                value={formData.limitPrice}
                onChange={handleChange}
                className="input"
                placeholder="Enter limit price"
                step="0.01"
                min="0.01"
                required
              />
            </div>
          )}

          {/* Stop Price */}
          {(formData.orderType === 'stop' || formData.orderType === 'stop_limit') && (
            <div>
              <label className="label">Stop Price ($)</label>
              <input
                type="number"
                name="stopPrice"
                value={formData.stopPrice}
                onChange={handleChange}
                className="input"
                placeholder="Enter stop price"
                step="0.01"
                min="0.01"
                required
              />
              <div className="flex items-center mt-2 text-sm text-gray-600">
                <FiInfo className="mr-1" />
                <span>Order triggers when price reaches ${formData.stopPrice}</span>
              </div>
            </div>
          )}

          {/* Limit Price for Stop-Limit */}
          {formData.orderType === 'stop_limit' && (
            <div>
              <label className="label">Limit Price ($)</label>
              <input
                type="number"
                name="limitPrice"
                value={formData.limitPrice}
                onChange={handleChange}
                className="input"
                placeholder="Enter limit price"
                step="0.01"
                min="0.01"
                required
              />
              <div className="flex items-center mt-2 text-sm text-gray-600">
                <FiInfo className="mr-1" />
                <span>After triggering, executes at ${formData.limitPrice} or better</span>
              </div>
            </div>
          )}

          {/* Trailing Percent */}
          {formData.orderType === 'trailing_stop' && (
            <div>
              <label className="label">Trailing Percent (%)</label>
              <input
                type="number"
                name="trailingPercent"
                value={formData.trailingPercent}
                onChange={handleChange}
                className="input"
                placeholder="Enter trailing percent"
                step="0.1"
                min="0.1"
                max="100"
                required
              />
              <div className="flex items-center mt-2 text-sm text-gray-600">
                <FiInfo className="mr-1" />
                <span>Stop price trails by {formData.trailingPercent || '0'}% from market peak</span>
              </div>
            </div>
          )}
        </div>

        {/* Order Summary */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="font-medium text-blue-900">Order Summary</h3>
              <p className="text-sm text-blue-700">
                {formData.side.toUpperCase()} {formData.quantity || '0'} shares of {symbol}
                {formData.orderType === 'limit' && ` at $${formData.limitPrice || '0.00'}`}
                {formData.orderType === 'stop' && ` stop at $${formData.stopPrice || '0.00'}`}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-blue-600">Estimated Cost</p>
              <p className="text-2xl font-bold text-blue-900">${calculateOrderValue()}</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex space-x-4">
          <button
            type="submit"
            disabled={loading}
            className={`btn btn-primary flex-1 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Placing Order...
              </span>
            ) : 'Place Order'}
          </button>
          
          <button
            type="button"
            onClick={() => {
              setFormData({
                orderType: 'limit',
                side: 'buy',
                quantity: '',
                limitPrice: '',
                stopPrice: '',
                trailingPercent: '',
              });
              setError('');
              setSuccess('');
            }}
            className="btn btn-secondary"
          >
            Reset
          </button>
        </div>

        {/* Alerts */}
        {error && (
          <div className="alert alert-error mt-4">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span>{error}</span>
            </div>
          </div>
        )}

        {success && (
          <div className="alert alert-success mt-4">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>{success}</span>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default OrderForm;